import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Slider,
  Typography,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  Collapse,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  SkipNext as StepIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
} from '@mui/icons-material';

type Granularity = 'day' | 'week' | 'month';

interface TemporalSliderProps {
  /** Earliest date in the dataset */
  minDate: Date;
  /** Latest date in the dataset */
  maxDate: Date;
  /** Current selected start date */
  startDate: Date;
  /** Current selected end date */
  endDate: Date;
  /** Active granularity */
  granularity: Granularity;
  /** Node count per time bucket */
  distribution: Record<string, number>;
  /** Called when the selected date range changes */
  onChange: (startDate: Date, endDate: Date) => void;
  /** Called when granularity changes */
  onGranularityChange: (granularity: Granularity) => void;
  /** Called on each playback tick */
  onPlaybackTick?: (startDate: Date, endDate: Date) => void;
}

/** Convert a Date to a slider value (millisecond timestamp) */
const dateToValue = (date: Date): number => date.getTime();

/** Convert a slider value back to a Date */
const valueToDate = (value: number): Date => new Date(value);

/** Format a date for display */
const formatDate = (date: Date, granularity: Granularity): string => {
  switch (granularity) {
    case 'month':
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
    case 'week':
    case 'day':
    default:
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }
};

/** Get the step size in milliseconds for a given granularity */
const getStepMs = (granularity: Granularity): number => {
  switch (granularity) {
    case 'day':
      return 24 * 60 * 60 * 1000;
    case 'week':
      return 7 * 24 * 60 * 60 * 1000;
    case 'month':
      return 30 * 24 * 60 * 60 * 1000; // Approximate
  }
};

/**
 * TemporalSlider Component
 *
 * Provides a dual-handle range slider for temporal graph filtering,
 * with a distribution histogram, playback controls, and granularity selection.
 */
const TemporalSlider: React.FC<TemporalSliderProps> = ({
  minDate,
  maxDate,
  startDate,
  endDate,
  granularity,
  distribution,
  onChange,
  onGranularityChange,
  onPlaybackTick,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [isExpanded, setIsExpanded] = useState(true);
  const playbackRef = useRef<NodeJS.Timeout | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Slider range values
  const minValue = dateToValue(minDate);
  const maxValue = dateToValue(maxDate);
  const currentRange: [number, number] = [dateToValue(startDate), dateToValue(endDate)];

  // Compute histogram bars from distribution
  const histogramBars = React.useMemo(() => {
    const entries = Object.entries(distribution).sort(([a], [b]) => a.localeCompare(b));
    if (entries.length === 0) return [];

    const maxCount = Math.max(...entries.map(([, count]) => count), 1);
    return entries.map(([key, count]) => ({
      key,
      count,
      height: (count / maxCount) * 100, // Percentage height
    }));
  }, [distribution]);

  // Debounced onChange handler
  const handleSliderChange = useCallback(
    (_: Event, value: number | number[]) => {
      if (!Array.isArray(value)) return;

      // Clear existing debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Debounce by 300ms to avoid rapid API calls
      debounceRef.current = setTimeout(() => {
        onChange(valueToDate(value[0]), valueToDate(value[1]));
      }, 300);
    },
    [onChange]
  );

  // Playback: advance the time window by one step
  const advancePlayback = useCallback(() => {
    const stepMs = getStepMs(granularity);
    const windowSize = endDate.getTime() - startDate.getTime();

    const newStart = new Date(startDate.getTime() + stepMs);
    const newEnd = new Date(newStart.getTime() + windowSize);

    // Stop if we've reached the end
    if (newEnd.getTime() > maxDate.getTime()) {
      setIsPlaying(false);
      return;
    }

    onChange(newStart, newEnd);
    if (onPlaybackTick) {
      onPlaybackTick(newStart, newEnd);
    }
  }, [startDate, endDate, maxDate, granularity, onChange, onPlaybackTick]);

  // Handle playback interval
  useEffect(() => {
    if (isPlaying) {
      const intervalMs = 1500 / playbackSpeed; // Base interval of 1.5s divided by speed
      playbackRef.current = setInterval(advancePlayback, intervalMs);
    } else {
      if (playbackRef.current) {
        clearInterval(playbackRef.current);
        playbackRef.current = null;
      }
    }

    return () => {
      if (playbackRef.current) {
        clearInterval(playbackRef.current);
      }
    };
  }, [isPlaying, playbackSpeed, advancePlayback]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Toggle playback
  const handlePlayPause = () => {
    setIsPlaying((prev) => !prev);
  };

  // Step forward one interval
  const handleStep = () => {
    setIsPlaying(false);
    advancePlayback();
  };

  // Cycle playback speed: 0.5x -> 1x -> 2x
  const handleSpeedToggle = () => {
    setPlaybackSpeed((prev) => {
      if (prev === 0.5) return 1;
      if (prev === 1) return 2;
      return 0.5;
    });
  };

  return (
    <Paper
      elevation={1}
      style={{
        marginLeft: 16,
        marginRight: 16,
        marginTop: 8,
        overflow: 'hidden',
        borderRadius: 4,
      }}
    >
      {/* Header / Toggle */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: 16,
          paddingRight: 16,
          paddingTop: 4,
          paddingBottom: 4,
          cursor: 'pointer',
          backgroundColor: '#fafafa',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <TimelineIcon fontSize="small" style={{ marginRight: 8, color: '#1976d2' }} />
        <Typography variant="subtitle2" style={{ flexGrow: 1 }}>
          Timeline
        </Typography>
        <Typography variant="caption" color="text.secondary" style={{ marginRight: 8 }}>
          {formatDate(startDate, granularity)} - {formatDate(endDate, granularity)}
        </Typography>
        {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
      </div>

      <Collapse in={isExpanded}>
        <div style={{ paddingLeft: 16, paddingRight: 16, paddingBottom: 16, paddingTop: 8 }}>
          {/* Histogram */}
          {histogramBars.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                height: 40,
                marginBottom: 4,
                gap: '1px',
              }}
            >
              {histogramBars.map((bar) => (
                <Tooltip key={bar.key} title={`${bar.key}: ${bar.count} nodes`}>
                  <div
                    style={{
                      flex: 1,
                      height: `${Math.max(bar.height, 4)}%`,
                      backgroundColor: '#42a5f5',
                      opacity: 0.6,
                      borderRadius: '2px 2px 0 0',
                      minWidth: 2,
                      transition: 'height 0.3s ease',
                    }}
                  />
                </Tooltip>
              ))}
            </div>
          )}

          {/* Dual-handle range slider */}
          <Slider
            value={currentRange}
            min={minValue}
            max={maxValue}
            step={getStepMs(granularity)}
            onChange={handleSliderChange}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => formatDate(valueToDate(value), granularity)}
            disableSwap
          />

          {/* Controls row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            {/* Playback controls */}
            <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
              <IconButton size="small" onClick={handlePlayPause} color={isPlaying ? 'primary' : 'default'}>
                {isPlaying ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Step forward">
              <IconButton size="small" onClick={handleStep}>
                <StepIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={`Speed: ${playbackSpeed}x`}>
              <IconButton size="small" onClick={handleSpeedToggle}>
                <SpeedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Typography variant="caption" color="text.secondary" style={{ marginLeft: 4 }}>
              {playbackSpeed}x
            </Typography>

            <div style={{ flexGrow: 1 }} />

            {/* Granularity selector */}
            <ToggleButtonGroup
              value={granularity}
              exclusive
              onChange={(_, val) => val && onGranularityChange(val)}
              size="small"
            >
              <ToggleButton value="day" style={{ paddingLeft: 12, paddingRight: 12, paddingTop: 2, paddingBottom: 2, fontSize: '0.7rem' }}>
                Day
              </ToggleButton>
              <ToggleButton value="week" style={{ paddingLeft: 12, paddingRight: 12, paddingTop: 2, paddingBottom: 2, fontSize: '0.7rem' }}>
                Week
              </ToggleButton>
              <ToggleButton value="month" style={{ paddingLeft: 12, paddingRight: 12, paddingTop: 2, paddingBottom: 2, fontSize: '0.7rem' }}>
                Month
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </div>
      </Collapse>
    </Paper>
  );
};

export default TemporalSlider;
